import React, { useState, useEffect } from 'react';
import { Phone, Shield, Clock, MapPin, ArrowRight, CheckCircle, Star, Leaf, Award, Heart, Mail, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import InterventionForm from '../components/InterventionForm';
import { track, whatsappUrl } from '../lib/track';

const NaturalHome = () => {
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    document.title = "Frelon33™ | Experts frelons & nuisibles en Gironde";
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(prev => ({
            ...prev,
            [entry.target.id]: true
          }));
        }
      });
    }, observerOptions);

    // Observe all sections
    document.querySelectorAll('[id^="section-"]').forEach(section => {
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const handleCall = () => {
    track('phone_click', { location: 'home' });
    window.location.href = 'tel:0676583038';
  };

  const scrollToContact = () => {
    document.getElementById('section-contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const advantages = [
    {
      icon: Award,
      title: "Expertise Certifiée",
      description: "Agréé par le Ministère de l'Agriculture, spécialiste depuis 2019",
      color: "text-green-600"
    },
    {
      icon: Clock,
      title: "Intervention Rapide",
      description: "Sous 48h maximum, disponible 7j/7 pour les urgences",
      color: "text-yellow-600"
    },
    {
      icon: Shield,
      title: "Sécurité Garantie",
      description: "Équipements professionnels, méthodes respectueuses de l'environnement",
      color: "text-blue-600"
    },
    {
      icon: Heart,
      title: "Tarifs Transparents",
      description: "Devis gratuit, prix fixes, pas de mauvaises surprises",
      color: "text-rose-600"
    }
  ];

  const steps = [
    {
      number: 1,
      title: "Contact",
      description: "Vous nous appelez ou remplissez notre formulaire",
      icon: "📞"
    },
    {
      number: 2,
      title: "Inspection",
      description: "Diagnostic gratuit et devis personnalisé sur place",
      icon: "🔍"
    },
    {
      number: 3,
      title: "Traitement",
      description: "Intervention sécurisée avec matériel professionnel",
      icon: "🛠️"
    },
    {
      number: 4,
      title: "Suivi",
      description: "Contrôle post-intervention et conseils préventifs",
      icon: "✅"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-green-50/30">
      
      {/* Hero Section Ultra-Moderne */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-green-50/50 via-yellow-50/30 to-white">
        {/* Motifs d'arrière-plan animés */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-40 h-40 rounded-full bg-gradient-to-r from-green-200 to-yellow-200 blur-3xl frelon-float"></div>
          <div className="absolute bottom-32 right-32 w-56 h-56 rounded-full bg-gradient-to-r from-yellow-200 to-green-200 blur-3xl frelon-float" style={{animationDelay: '2s'}}></div>
          <div className="absolute top-1/2 left-10 w-24 h-24 rounded-full bg-gradient-to-r from-stone-200 to-green-100 blur-2xl frelon-float" style={{animationDelay: '4s'}}></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center min-h-[80vh]">
            
            {/* Hero Content Ultra-Moderne */}
            <div className="space-y-8 text-center lg:text-left frelon-slide-up">
              <div className="space-y-6">
                <div className="inline-flex items-center space-x-3 bg-gradient-to-r from-green-100 to-yellow-100 text-green-800 px-6 py-3 rounded-full text-sm font-semibold shadow-lg backdrop-blur-sm border border-green-200/50">
                  <Leaf className="h-4 w-4 animate-pulse" />
                  <span>Intervention écologique et sécurisée</span>
                </div>
                
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-black leading-tight frelon-slide-up">
                  <span className="block text-gray-900">L'expert frelon</span>
                  <span className="block frelon-heading bg-gradient-to-r from-green-600 to-yellow-500 bg-clip-text text-transparent">
                    en Gironde
                  </span>
                  <span className="block text-gray-900">rapide & garanti</span>
                </h1>
                
                <p className="text-xl md:text-2xl text-gray-600 leading-relaxed max-w-2xl font-medium">
                  <span className="frelon33-brand">Frelon33™</span> - Destruction professionnelle de nids de frelons asiatiques et européens. 
                  <strong className="text-green-700 font-bold"> Intervention sous 48h</strong>, 
                  7j/7 sur toute la Gironde.
                </p>
              </div>

              {/* CTA Buttons Ultra-Modernes */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button
                  onClick={scrollToContact}
                  className="frelon-btn frelon-btn-primary text-lg px-8 py-4 rounded-2xl font-bold shadow-2xl flex items-center justify-center space-x-3"
                >
                  <span>Demander une intervention rapide</span>
                  <ArrowRight className="h-5 w-5" />
                </button>
                
                <button
                  onClick={handleCall}
                  className="frelon-btn frelon-btn-secondary text-lg px-8 py-4 rounded-2xl font-bold flex items-center justify-center space-x-3"
                  data-testid="hero-call-button"
                  data-gtm-event="phone_click"
                >
                  <Phone className="h-5 w-5" />
                  <span>06 76 58 30 38</span>
                </button>
              </div>

              {/* Trust Indicators Modernes */}
              <div className="flex flex-wrap gap-6 justify-center lg:justify-start text-sm">
                {[
                  { icon: CheckCircle, text: "Agréé Ministère" },
                  { icon: CheckCircle, text: "Devis gratuit" },
                  { icon: CheckCircle, text: "Garantie résultat" },
                  { icon: CheckCircle, text: "Respectueux nature" }
                ].map((item, index) => (
                  <div key={index} className="flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-md border border-green-100">
                    <item.icon className="h-4 w-4 text-green-500" />
                    <span className="font-medium text-gray-700">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Hero Image Ultra-Moderne */}
            <div className="relative frelon-slide-up" style={{animationDelay: '0.3s'}}>
              <div className="relative z-10 transform hover:scale-105 transition-all duration-700 frelon-zoom-gentle">
                <div className="relative overflow-hidden rounded-3xl shadow-2xl frelon-depth-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_frelon33-experts/artifacts/xehwbzjb_pulv%C3%A9riser.png"
                    alt="Technicien Frelon33™ en intervention de destruction de nid de frelons en Gironde" 
                    className="w-full h-auto"
                    fetchPriority="high"
                    decoding="async"
                    width="640"
                    height="640"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
                </div>
                
                {/* Badge Flottant Ultra-Moderne */}
                <div className="absolute -bottom-8 -left-8 frelon-card p-6 border-0 frelon-glow">
                  <div className="flex items-center space-x-4">
                    <div className="bg-gradient-to-r from-green-100 to-yellow-100 p-4 rounded-2xl">
                      <Shield className="h-8 w-8 text-green-600" />
                    </div>
                    <div>
                      <p className="font-black text-gray-900 text-lg">Intervention sécurisée</p>
                      <p className="text-green-600 font-semibold">Équipement professionnel</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-green-200/40 to-yellow-200/40 rounded-3xl blur-3xl -z-10 transform scale-110 frelon-glow"></div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator Moderne */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="bg-white/80 backdrop-blur-sm p-3 rounded-full shadow-lg">
            <ArrowRight className="h-6 w-6 text-green-600 transform rotate-90" />
          </div>
        </div>
      </section>

      {/* Section Avantages Ultra-Moderne */}
      <section id="section-advantages" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-1000 ${
            isVisible['section-advantages'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-6">
              Pourquoi choisir <span className="frelon33-brand">Frelon33™</span> ?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-medium">
              Notre expertise au service de votre tranquillité, dans le respect de l'environnement
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {advantages.map((advantage, index) => {
              const Icon = advantage.icon;
              return (
                <div 
                  key={index} 
                  className={`frelon-card hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-6 group ${
                    isVisible['section-advantages'] 
                      ? 'opacity-100 translate-y-0' 
                      : 'opacity-0 translate-y-12'
                  }`}
                  style={{ transitionDelay: `${index * 150}ms` }}
                >
                  <div className="text-center">
                    <div className="bg-gradient-to-r from-stone-100 to-green-100 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-all duration-300 frelon-glow">
                      <Icon className={`h-10 w-10 ${advantage.color}`} />
                    </div>
                    <h3 className="text-xl font-black text-gray-900 mb-4">
                      {advantage.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed font-medium">
                      {advantage.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section Zone d'intervention - Gironde */}
      <section id="section-zone" className="py-20 bg-gradient-to-br from-stone-50 to-green-50/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center transition-all duration-1000 ${
            isVisible['section-zone'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}>
            {/* Représentation légère de la zone (SVG, sans API lourde) */}
            <div className="relative flex items-center justify-center">
              <div className="relative w-full max-w-md aspect-square">
                <svg viewBox="0 0 400 400" className="w-full h-full" role="img" aria-label="Carte illustrée de la zone d'intervention en Gironde">
                  <defs>
                    <radialGradient id="zoneGrad" cx="50%" cy="50%" r="50%">
                      <stop offset="0%" stopColor="#7A9E7E" stopOpacity="0.35" />
                      <stop offset="100%" stopColor="#7A9E7E" stopOpacity="0" />
                    </radialGradient>
                  </defs>
                  {/* Cercles concentriques = rayon d'intervention */}
                  <circle cx="200" cy="200" r="180" fill="url(#zoneGrad)" />
                  <circle cx="200" cy="200" r="180" fill="none" stroke="#2D5016" strokeOpacity="0.12" strokeWidth="1" />
                  <circle cx="200" cy="200" r="120" fill="none" stroke="#2D5016" strokeOpacity="0.15" strokeWidth="1" strokeDasharray="4 6" />
                  <circle cx="200" cy="200" r="60" fill="none" stroke="#2D5016" strokeOpacity="0.18" strokeWidth="1" strokeDasharray="4 6" />
                  {/* Points de communes */}
                  {[
                    { x: 200, y: 200, label: 'Cadaujac', base: true },
                    { x: 175, y: 130, label: 'Bordeaux' },
                    { x: 120, y: 150, label: 'Mérignac' },
                    { x: 150, y: 245, label: 'Pessac' },
                    { x: 235, y: 250, label: 'Villenave' },
                    { x: 270, y: 160, label: 'Cenon' },
                    { x: 250, y: 110, label: 'Lormont' },
                  ].map((p) => (
                    <g key={p.label}>
                      <circle cx={p.x} cy={p.y} r={p.base ? 9 : 5} fill={p.base ? '#F4B942' : '#2D5016'} />
                      {p.base && <circle cx={p.x} cy={p.y} r="16" fill="none" stroke="#F4B942" strokeWidth="2" className="frelon-glow" />}
                    </g>
                  ))}
                </svg>
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 translate-y-6 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full shadow-md text-sm font-bold text-green-800 flex items-center gap-1">
                  <MapPin className="h-4 w-4 text-yellow-500" /> Cadaujac
                </div>
              </div>
            </div>

            {/* Texte + communes */}
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-semibold">
                <MapPin className="h-4 w-4" /> Entreprise locale en Gironde
              </div>
              <h2 className="text-3xl md:text-4xl font-black text-gray-900">
                Basés à <span className="frelon33-brand">Cadaujac</span>, nous intervenons dans toute la Gironde
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                <span className="frelon33-brand font-semibold">Frelon33™</span> est proche de chez vous :
                Bordeaux, sa métropole et l'ensemble du département. Un professionnel local, réactif,
                qui connaît le terrain.
              </p>
              <div className="flex flex-wrap gap-2">
                {['Cadaujac', 'Bordeaux', 'Mérignac', 'Pessac', 'Talence', 'Villenave-d\'Ornon', 'Bègles', 'Gradignan', 'Cenon', 'Lormont'].map((c) => (
                  <span key={c} className="bg-white border border-green-100 text-gray-700 px-3 py-1.5 rounded-full text-sm font-medium shadow-sm">
                    {c}
                  </span>
                ))}
                <span className="bg-green-600 text-white px-3 py-1.5 rounded-full text-sm font-semibold shadow-sm">
                  + toute la Gironde
                </span>
              </div>
              <button
                onClick={scrollToContact}
                className="frelon-btn frelon-btn-primary text-lg px-6 py-3 rounded-xl font-bold text-white inline-flex items-center gap-2"
                data-testid="zone-cta-button"
              >
                <span>Vérifier ma commune</span>
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Section Contact Ultra-Moderne avec Mailto */}
      <section id="section-contact" className="py-20 bg-gradient-to-br from-green-50/50 to-stone-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-1000 ${
            isVisible['section-contact'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-6">
              Demander une <span className="frelon33-brand">intervention</span>
            </h2>
            <p className="text-xl text-gray-600 font-medium">
              Contactez-nous directement pour une réponse rapide et un devis gratuit
            </p>
          </div>

          <div className={`transition-all duration-1000 ${
            isVisible['section-contact'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}>
            {/* Bandeau appel direct */}
            <div className="frelon-card border-0 frelon-depth-2 mb-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
              <div className="flex items-center gap-4">
                <div className="bg-gradient-to-r from-green-100 to-yellow-100 w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Phone className="h-7 w-7 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Une urgence ? Appelez directement</h3>
                  <p className="text-gray-600 text-sm">Réponse immédiate, 7j/7 sur toute la Gironde</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <button
                  onClick={handleCall}
                  className="frelon-btn frelon-btn-primary text-lg px-6 py-3 rounded-xl font-bold text-white flex items-center gap-2 w-full sm:w-auto justify-center"
                  data-testid="home-contact-call-button"
                  data-gtm-event="phone_click"
                >
                  <Phone className="h-5 w-5" />
                  06 76 58 30 38
                </button>
                <a
                  href={whatsappUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => track('whatsapp_click', { location: 'home' })}
                  className="text-lg px-6 py-3 rounded-xl font-bold text-white flex items-center gap-2 w-full sm:w-auto justify-center transition-transform duration-300 hover:scale-105"
                  style={{ backgroundColor: '#25D366' }}
                  data-testid="home-contact-whatsapp-button"
                  data-gtm-event="whatsapp_click"
                >
                  <MessageCircle className="h-5 w-5" />
                  WhatsApp
                </a>
              </div>
            </div>

            {/* Formulaire de demande d'intervention (Netlify Forms) */}
            <InterventionForm />
          </div>

        </div>
      </section>
    </div>
  );
};

export default NaturalHome;